import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
  IconButton,
  Collapse,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  AccessTime as TimeIcon,
  MonetizationOn as CostIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { generateLiveKitToken } from '../api/livekit';

// Import LiveKit components and styles
import '@livekit/components-styles';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRemoteParticipants,
  useConnectionState,
  useLocalParticipant,
  useTrackTranscription
} from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';

// Small Audio Visualizer next to the call button
const SmallAudioVisualizer = ({ audioTrack, isActive, isConnected }) => {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const [hasAudio, setHasAudio] = useState(false);

  useEffect(() => {
    if (!isActive || !isConnected || !audioTrack?.mediaStreamTrack) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      setHasAudio(false);
      return;
    }

    const setupAudioAnalyser = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.7;
        analyserRef.current = analyser;
        
        const mediaStreamTrack = audioTrack.mediaStreamTrack;
        const stream = new MediaStream([mediaStreamTrack]);
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        
        drawVisualizer();
      } catch (error) {
        console.error('Error setting up audio visualizer:', error);
      }
    };

    const drawVisualizer = () => {
      if (!canvasRef.current || !analyserRef.current) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const analyser = analyserRef.current;
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const draw = () => {
        if (!analyserRef.current) return;
        animationRef.current = requestAnimationFrame(draw);
        
        analyser.getByteFrequencyData(dataArray);
        
        // Lower threshold for better sensitivity
        const averageAmplitude = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
        const audioThreshold = 3; // Much lower threshold
        const currentHasAudio = averageAmplitude > audioThreshold;
        
        // Debug logging
        if (currentHasAudio !== hasAudio) {
          console.log('Audio activity changed:', currentHasAudio, 'Average amplitude:', averageAmplitude);
        }
        
        setHasAudio(currentHasAudio);
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Always draw something when connected, even if minimal
        if (isConnected) {
          const barWidth = 3;
          const barGap = 1;
          const maxBarHeight = canvas.height - 4;
          const numBars = Math.floor(canvas.width / (barWidth + barGap));
          
          for (let i = 0; i < numBars; i++) {
            const dataIndex = Math.floor((i / numBars) * bufferLength);
            const amplitude = currentHasAudio ? dataArray[dataIndex] / 255 : 0.1; // Minimum visualization
            
            const barHeight = Math.max(2, amplitude * maxBarHeight);
            const x = i * (barWidth + barGap);
            const y = (canvas.height - barHeight) / 2;
            
            // Create gradient effect
            const alpha = currentHasAudio ? 0.3 + amplitude * 0.7 : 0.2;
            ctx.fillStyle = `rgba(244, 67, 54, ${alpha})`;
            ctx.fillRect(x, y, barWidth, barHeight);
          }
        }
      };
      
      draw();
    };

    setupAudioAnalyser();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      setHasAudio(false);
    };
  }, [audioTrack, isActive, isConnected]);

  // Show when connected, not just when there's audio
  if (!isConnected) return null;

  return (
    <canvas 
      ref={canvasRef} 
      width={40} 
      height={20} 
      style={{ 
        borderRadius: '4px',
        backgroundColor: hasAudio ? 'rgba(244, 67, 54, 0.1)' : 'rgba(200, 200, 200, 0.1)',
        border: hasAudio ? '1px solid rgba(244, 67, 54, 0.2)' : '1px solid rgba(200, 200, 200, 0.2)',
        transition: 'all 0.2s ease'
      }}
    />
  );
};

// Compact Usage Timer Component
const CompactUsageTimer = ({ startTime, isActive, cost = 0.15 }) => {
  const [elapsed, setElapsed] = useState(0);
  const [currentCost, setCurrentCost] = useState(0);

  useEffect(() => {
    if (!isActive || !startTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      const elapsedMinutes = elapsedSeconds / 60;
      setElapsed(elapsedSeconds);
      setCurrentCost(elapsedMinutes * cost);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, startTime, cost]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isActive) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={<TimeIcon />}
        label={formatTime(elapsed)}
        color="primary"
        variant="outlined"
        size="small"
        sx={{ fontSize: '0.7rem', height: 24 }}
      />
      <Chip
        icon={<CostIcon />}
        label={`€${currentCost.toFixed(3)}`}
        color="secondary"
        variant="outlined"
        size="small"
        sx={{ fontSize: '0.7rem', height: 24 }}
      />
    </Box>
  );
};

// Compact Conversation Display
const CompactConversationDisplay = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState([]);
  const [transcriptMap, setTranscriptMap] = useState(new Map());
  const messagesEndRef = useRef(null);
  
  const remoteParticipants = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  
  const agentParticipant = remoteParticipants.length > 0 ? remoteParticipants[0] : null;
  
  const agentAudioTrackRef = agentParticipant ? {
    participant: agentParticipant,
    source: Track.Source.Microphone,
    publication: agentParticipant.getTrackPublications().find(
      pub => pub.source === Track.Source.Microphone
    )
  } : null;
  
  const localAudioTrackRef = localParticipant ? {
    participant: localParticipant,
    source: Track.Source.Microphone,
    publication: localParticipant.getTrackPublications().find(
      pub => pub.source === Track.Source.Microphone
    )
  } : null;
  
  const agentTranscription = useTrackTranscription(agentAudioTrackRef);
  const localTranscription = useTrackTranscription(localAudioTrackRef);

  // Process transcriptions
  useEffect(() => {
    if (agentTranscription?.segments) {
      const updatedMap = new Map(transcriptMap);
      let hasChanges = false;
      
      agentTranscription.segments.forEach(segment => {
        const existingMsg = updatedMap.get(segment.id);
        const newText = segment.final ? segment.text : `${segment.text}...`;
        
        if (!existingMsg || existingMsg.text !== newText) {
          updatedMap.set(segment.id, {
            text: newText,
            isAgent: true,
            timestamp: existingMsg?.timestamp || Date.now(),
            isFinal: segment.final
          });
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        setTranscriptMap(updatedMap);
        updateMessages(updatedMap);
      }
    }
  }, [agentTranscription?.segments]);
  
  useEffect(() => {
    if (localTranscription?.segments) {
      const updatedMap = new Map(transcriptMap);
      let hasChanges = false;
      
      localTranscription.segments.forEach(segment => {
        const existingMsg = updatedMap.get(segment.id);
        const newText = segment.final ? segment.text : `${segment.text}...`;
        
        if (!existingMsg || existingMsg.text !== newText) {
          updatedMap.set(segment.id, {
            text: newText,
            isAgent: false,
            timestamp: existingMsg?.timestamp || Date.now(),
            isFinal: segment.final
          });
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        setTranscriptMap(updatedMap);
        updateMessages(updatedMap);
      }
    }
  }, [localTranscription?.segments]);

  const updateMessages = (map) => {
    const messageArray = Array.from(map.values());
    messageArray.sort((a, b) => a.timestamp - b.timestamp);
    setMessages(messageArray.slice(-10)); // Keep only last 10 messages
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Chat Toggle Button */}
      <IconButton
        onClick={onToggle}
        size="small"
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          bgcolor: isOpen ? alpha('#2196f3', 0.2) : alpha('#2196f3', 0.1),
          borderColor: '#2196f3',
          color: '#2196f3',
          border: '1px solid',
          '&:hover': {
            bgcolor: alpha('#2196f3', 0.3)
          }
        }}
      >
        <ChatIcon fontSize="small" />
      </IconButton>

      {/* Expandable Conversation Panel */}
      <Collapse in={isOpen}>
        <Paper 
          elevation={3}
          sx={{ 
            position: 'fixed',
            top: '10%', // Changed from 20% to 10% - even higher
            left: '50%',
            transform: 'translate(-50%, 0)',
            width: 350,
            maxWidth: '90vw',
            height: 350, // Reduced height
            maxHeight: '60vh', // Reduced max height
            zIndex: 9999,
            border: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)' // Enhanced shadow for better visibility
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'primary.main', 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Live Conversation
            </Typography>
            <IconButton size="small" onClick={onToggle} sx={{ color: 'white' }}>
              <ExpandLessIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          {/* Messages Container */}
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 2,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}>
              {messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                  Start talking to see the conversation...
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {messages.map((message, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 600,
                          color: message.isAgent ? 'primary.main' : 'secondary.main',
                          minWidth: 45,
                          fontSize: '0.7rem',
                          flexShrink: 0
                        }}
                      >
                        {message.isAgent ? 'Agent:' : 'You:'}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: message.isFinal ? 1 : 0.7,
                          fontStyle: message.isFinal ? 'normal' : 'italic',
                          fontSize: '0.75rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {message.text}
                      </Typography>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

// LiveKit Room Content Component
const LiveKitRoomContent = ({ onDisconnect }) => {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const connectionState = useConnectionState();
  const [isMuted, setIsMuted] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  
  const agentParticipant = remoteParticipants.length > 0 ? remoteParticipants[0] : null;
  const agentAudioTrack = agentParticipant ? 
    agentParticipant.getTrackPublications().find(pub => pub.source === Track.Source.Microphone)?.track 
    : null;

  // Update mute state when local participant changes
  useEffect(() => {
    if (localParticipant) {
      const updateMuteState = () => {
        const micTrack = localParticipant.getTrackPublication(Track.Source.Microphone);
        setIsMuted(!micTrack?.isEnabled);
      };
      
      updateMuteState();
      
      localParticipant.on('trackMuted', updateMuteState);
      localParticipant.on('trackUnmuted', updateMuteState);
      localParticipant.on('trackPublished', updateMuteState);
      localParticipant.on('trackUnpublished', updateMuteState);
      
      return () => {
        localParticipant.off('trackMuted', updateMuteState);
        localParticipant.off('trackUnmuted', updateMuteState);
        localParticipant.off('trackPublished', updateMuteState);
        localParticipant.off('trackUnpublished', updateMuteState);
      };
    }
  }, [localParticipant]);
  
  const toggleMicrophone = async () => {
    if (localParticipant) {
      try {
        await localParticipant.setMicrophoneEnabled(isMuted);
        setIsMuted(!isMuted);
      } catch (error) {
        console.error('Error toggling microphone:', error);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Conversation Toggle */}
      <CompactConversationDisplay 
        isOpen={showConversation}
        onToggle={() => setShowConversation(!showConversation)}
      />
      
      {/* Mute Button */}
      <Button
        onClick={toggleMicrophone}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 36,
          height: 32,
          borderRadius: 2,
          bgcolor: isMuted ? alpha('#f44336', 0.1) : alpha('#2196f3', 0.1),
          borderColor: isMuted ? '#f44336' : '#2196f3',
          color: isMuted ? '#f44336' : '#2196f3',
          '&:hover': {
            bgcolor: isMuted ? alpha('#f44336', 0.2) : alpha('#2196f3', 0.2),
            borderColor: isMuted ? '#f44336' : '#2196f3'
          }
        }}
      >
        {isMuted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
      </Button>
      
      {/* Small Audio Visualizer */}
      <SmallAudioVisualizer 
        audioTrack={agentAudioTrack}
        isActive={connectionState === ConnectionState.Connected}
        isConnected={connectionState === ConnectionState.Connected}
      />
      
      {/* End Call Button */}
      <Button
        onClick={onDisconnect}
        variant="contained"
        color="error"
        size="small"
        sx={{
          minWidth: 80,
          height: 32,
          borderRadius: 2,
          fontWeight: 600,
          '&:hover': {
            transform: 'scale(1.02)'
          },
          transition: 'all 0.2s ease'
        }}
      >
        End Call
      </Button>
      
      <RoomAudioRenderer />
    </Box>
  );
};

// Main Compact Agent Playground Component
const IntegratedAgentPlayground = ({ agent, onUsageUpdate, autoStart = false }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [token, setToken] = useState(null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [conversationOpen, setConversationOpen] = useState(autoStart);
  const [hasAttemptedAutoStart, setHasAttemptedAutoStart] = useState(false);
  
  // Auto-start effect with better logging and dependency management
  useEffect(() => {
    console.log('IntegratedAgentPlayground useEffect triggered:', {
      autoStart,
      agent: agent?.id,
      agentName: agent?.name,
      isPlugnPlay: agent?.is_plugnplay,
      isConnected,
      isConnecting,
      hasAttemptedAutoStart,
      user: user?.id
    });
    
    if (autoStart && agent && agent.id && user && !isConnected && !isConnecting && !hasAttemptedAutoStart) {
      console.log('🚀 Auto-starting test for PlugnPlay agent:', agent.name);
      setHasAttemptedAutoStart(true);
      handleConnect();
    }
  }, [autoStart, agent?.id, agent?.name, user?.id, isConnected, isConnecting, hasAttemptedAutoStart]);
  
  // Reset auto-start attempt when agent changes
  useEffect(() => {
    setHasAttemptedAutoStart(false);
    
    // Log agent information when it changes
    if (agent) {
      console.log('🔄 AGENT CHANGED IN PLAYGROUND:');
      console.log('   New Agent ID:', agent.id);
      console.log('   New Agent Name:', agent.name);
      console.log('   Is PlugnPlay:', agent.is_plugnplay);
      console.log('   This will be used as agent_name for LiveKit dispatch');
    } else {
      console.log('🔄 Agent cleared from playground');
    }
  }, [agent?.id]);

  const handleConnect = async () => {
    if (!agent || !user) {
      console.warn('Cannot connect: missing agent or user', { agent: agent?.id, user: user?.id });
      return;
    }
    
    // Log the agent ID that will become the agent_name for LiveKit dispatch
    console.log('🎯 AGENT IDENTIFICATION FOR PLAYGROUND:');
    console.log('   Agent ID (becomes agent_name):', agent.id);
    console.log('   Agent Name (display):', agent.name);
    console.log('   Is PlugnPlay:', agent.is_plugnplay);
    console.log('   This ID will be set as AGENT_PLUGNPLAY_ID in backend');
    
    console.log('🔗 Starting connection to agent:', {
      agentId: agent.id,
      agentName: agent.name,
      isPlugnPlay: agent.is_plugnplay,
      userId: user.id
    });
    
    setIsConnecting(true);
    setError(null);
    
    try {
      console.log('📞 Calling generateLiveKitToken with agent.id:', agent.id);
      console.log('🎯 This agent.id will be used as AGENT_ID for LiveKit dispatch');
      const response = await generateLiveKitToken(agent.id, null, agent.is_plugnplay);
      
      console.log('✅ LiveKit token response:', response);
      
      if (response.token && response.server_url) {
        setToken(response.token);
        setUrl(response.server_url);
        setIsConnected(true);
        setStartTime(new Date());
        setConversationOpen(true); // Open conversation when connected
        
        console.log('🎉 Successfully connected to agent!');
        console.log('📍 Room name (contains agent ID):', response.room_name || 'Not provided');
        console.log('🌐 Server URL:', response.server_url);
        
        if (onUsageUpdate) {
          onUsageUpdate({
            agentId: agent.id,
            startTime: new Date(),
            isActive: true
          });
        }
      } else {
        const errorMsg = response.error || 'Failed to generate token - missing token or server URL';
        console.error('❌ Token generation failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('❌ Error connecting to LiveKit:', err);
      setError(`Failed to connect: ${err.message}`);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsConnected(false);
    setToken(null);
    setUrl('');
    setStartTime(null);
    
    if (onUsageUpdate) {
      onUsageUpdate({
        agentId: agent.id,
        startTime: null,
        isActive: false
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Usage Timer */}
      {isConnected && (
        <CompactUsageTimer 
          startTime={startTime} 
          isActive={isConnected}
          cost={0.15}
        />
      )}
      
      {/* Test/Connection Controls */}
      {isConnected && token && url ? (
        <LiveKitRoom
          token={token}
          serverUrl={url}
          connect={true}
          audio={true}
          video={false}
          style={{ display: 'contents' }}
        >
          <LiveKitRoomContent onDisconnect={handleDisconnect} />
        </LiveKitRoom>
      ) : (
        <Button
          variant="contained"
          onClick={handleConnect}
          disabled={isConnecting}
          startIcon={isConnecting ? <CircularProgress size={16} /> : <PhoneIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 100,
            height: 32,
            bgcolor: 'success.main',
            '&:hover': {
              bgcolor: 'success.dark',
              transform: 'scale(1.02)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {isConnecting ? 'Connecting...' : 'Test Agent'}
        </Button>
      )}
      
      {/* Error Display */}
      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default IntegratedAgentPlayground;
